// Phase II · Wave E.1a (base) + Wave E.1b (auto-open hook + server sync) · CurrencySwitcher.
//
// Simple monochrome V12 dropdown that lets the user override the
// auto-derived currency. Sits next to the LocaleSwitcher in the header.
//
// E.1b additives:
//   * Reads `?openCurrency=1` URL param on mount and auto-opens the
//     dropdown so geo-redirected buyers see the matching currency
//     suggestion immediately. Fires the
//     `currency_switcher_auto_opened` banner event once per load.
//   * On change, also mirrors the preference to the server
//     (`PUT /api/v9/user/preferences/currency`) when the user is
//     authenticated · cross-device sync.
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
    SUPPORTED_CURRENCIES, CURRENCY_SYMBOL,
} from "@/i18n/currencies";
import { useCurrency } from "@/i18n/CurrencyContext";
import { apiFetch } from "@/lib/api";


function emitBannerEventSafe(body) {
    try {
        apiFetch("/v9/i18n/geo-banner/event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    } catch { /* best-effort analytics */ }
}


export default function CurrencySwitcher({ compact = false }) {
    const { currency, setCurrency, locale } = useCurrency();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const autoOpenFiredRef = useRef(false);
    const originCurrencyRef = useRef(currency);

    const toggle = useCallback(() => setOpen((v) => !v), []);
    const close = useCallback(() => setOpen(false), []);

    // Click-outside handler.
    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) close();
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open, close]);

    // Phase II · Wave E.1b · auto-open from `?openCurrency=1`.
    useEffect(() => {
        if (autoOpenFiredRef.current) return;
        if (typeof window === "undefined") return;
        try {
            const sp = new URLSearchParams(window.location.search || "");
            if (sp.get("openCurrency") === "1") {
                autoOpenFiredRef.current = true;
                setOpen(true);
                originCurrencyRef.current = currency;
                emitBannerEventSafe({
                    suggested_locale: locale || "en-US",
                    current_locale:   locale || "en-US",
                    event_type: "currency_switcher_auto_opened",
                });
                // Strip the param so a reload doesn't re-fire.
                try {
                    const url = new URL(window.location.href);
                    url.searchParams.delete("openCurrency");
                    window.history.replaceState({}, "", url.toString());
                } catch { /* history disabled */ }
            }
        } catch { /* URL parse failed */ }
    }, [currency, locale]);

    const handleSelect = useCallback((code) => {
        if (!code) return;
        const prev = currency;
        setCurrency(code);
        close();
        // E.1b · sync server-side (best-effort · auth required · 401 silent).
        try {
            apiFetch("/v9/user/preferences/currency", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ currency: code }),
            });
        } catch { /* ignore · anonymous sessions skip sync */ }
        // E.1b · if the change came after an auto-open, log the conversion.
        if (autoOpenFiredRef.current && code !== originCurrencyRef.current) {
            emitBannerEventSafe({
                suggested_locale: locale || "en-US",
                current_locale:   locale || "en-US",
                event_type: "currency_switcher_changed_from_auto_open",
            });
            // Guard · fire only the first conversion per load.
            autoOpenFiredRef.current = false;
        }
    }, [currency, locale, setCurrency, close]);

    return (
        <div
            ref={ref}
            className="relative inline-block text-left"
            data-testid="currency-switcher"
        >
            <button
                type="button"
                onClick={toggle}
                aria-haspopup="listbox"
                aria-expanded={open}
                data-testid="currency-switcher-trigger"
                className={
                    "inline-flex items-center gap-1 rounded-md border "
                    + "border-neutral-700 bg-transparent text-neutral-200 "
                    + "hover:bg-neutral-900 transition "
                    + (compact ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-sm")
                }
            >
                <span className="font-medium">
                    {CURRENCY_SYMBOL[currency] || currency}
                </span>
                <span className="text-neutral-400">{currency}</span>
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                    <path d="M1 3 L5 7 L9 3" stroke="currentColor"
                          strokeWidth="1.4" fill="none" />
                </svg>
            </button>
            {open && (
                <ul
                    role="listbox"
                    data-testid="currency-switcher-menu"
                    className={
                        "absolute right-0 mt-1 min-w-[9rem] max-h-72 "
                        + "overflow-y-auto rounded-md border border-neutral-800 "
                        + "bg-neutral-950 py-1 shadow-lg shadow-black/40 z-50"
                    }
                >
                    {SUPPORTED_CURRENCIES.map((code) => (
                        <li
                            key={code}
                            role="option"
                            aria-selected={code === currency}
                            tabIndex={0}
                            data-testid={`currency-switcher-option-${code}`}
                            onClick={() => handleSelect(code)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    handleSelect(code);
                                }
                            }}
                            className={
                                "cursor-pointer px-3 py-1.5 text-sm "
                                + "text-neutral-200 hover:bg-neutral-900 "
                                + "flex items-center justify-between gap-3 "
                                + (code === currency ? "font-medium" : "")
                            }
                        >
                            <span className="flex items-center gap-2">
                                <span className="w-5 text-neutral-500">
                                    {CURRENCY_SYMBOL[code] || "·"}
                                </span>
                                <span>{code}</span>
                            </span>
                            {code === currency && (
                                <span
                                    aria-hidden="true"
                                    className="text-xs text-neutral-500"
                                >
                                    ✓
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
